import type { ExternalTestStep } from '@chatsdet/types';
import type { StepVerdict } from '../../../features/external-tests/scenarios';

export type StepFilter = 'all' | 'failures';

export type NumberedStep = { step: ExternalTestStep; number: number };

export type StepGroup = {
  key: 'background' | 'scenario' | 'steps';
  /** Section label; null when the test has no BDD structure to group by. */
  label: 'Background' | 'Scenario' | null;
  /** Steps in the group before the filter is applied. */
  total: number;
  steps: NumberedStep[];
};

/**
 * Split a test's steps into Background / Scenario groups with one continuous
 * step number across both. A test with no BDD steps is a single unlabelled
 * group. Groups left empty by the filter are dropped.
 */
export function groupNativeSteps(steps: ExternalTestStep[], filter: StepFilter): StepGroup[] {
  const numbered = steps.map((step, i) => ({ step, number: i + 1 }));
  const keep = (entries: NumberedStep[]) =>
    filter === 'failures' ? entries.filter(({ step }) => step.status === 'failed') : entries;

  const make = (
    key: StepGroup['key'],
    label: StepGroup['label'],
    entries: NumberedStep[]
  ): StepGroup => ({ key, label, total: entries.length, steps: keep(entries) });

  const groups: StepGroup[] = steps.some((step) => step.bdd)
    ? [
        make(
          'background',
          'Background',
          numbered.filter(({ step }) => step.bdd?.background === true)
        ),
        make(
          'scenario',
          'Scenario',
          numbered.filter(({ step }) => step.bdd?.background !== true)
        ),
      ]
    : [make('steps', null, numbered)];

  return groups.filter((group) => group.total > 0 && group.steps.length > 0);
}

export type StepOutcome = 'passed' | 'failed' | 'flaky' | 'skipped';

/** Exhaustive on purpose: a new verdict must be given an outcome here. */
export function stepOutcomeOf(verdict: StepVerdict): StepOutcome {
  switch (verdict) {
    case 'passed':
      return 'passed';
    case 'failed':
      return 'failed';
    case 'flaky':
      return 'flaky';
    case 'skipped':
      return 'skipped';
  }
}

export type StepProgress = {
  total: number;
  passed: number;
  failed: number;
  flaky: number;
  skipped: number;
  /** 1-based number of the first failed step. */
  firstFailedNumber: number | null;
};

export function summarizeStepProgress(outcomes: StepOutcome[]): StepProgress {
  const progress: StepProgress = {
    total: outcomes.length,
    passed: 0,
    failed: 0,
    flaky: 0,
    skipped: 0,
    firstFailedNumber: null,
  };
  outcomes.forEach((outcome, i) => {
    progress[outcome] += 1;
    if (outcome === 'failed' && progress.firstFailedNumber === null) {
      progress.firstFailedNumber = i + 1;
    }
  });
  return progress;
}

// eslint-disable-next-line no-control-regex
const ANSI = /\u001b\[[0-9;]*m/g;

export const stripAnsi = (text: string) => text.replace(ANSI, '');

/**
 * Pull Expected/Actual out of an assertion message, but only for the obvious
 * `Expected: …` plus `Received: …` / `Actual: …` line shape. Anything else
 * returns null and the raw message is all the viewer sees.
 */
export function parseExpectedActual(
  message: string
): { expected: string; actual: string } | null {
  const clean = stripAnsi(message);
  const expected = /^\s*Expected(?: (?:string|value|substring|pattern))?:\s*(.+)$/im.exec(clean);
  const actual = /^\s*(?:Received|Actual)(?: (?:string|value))?:\s*(.+)$/im.exec(clean);
  if (!expected?.[1] || !actual?.[1]) return null;
  return { expected: expected[1].trim(), actual: actual[1].trim() };
}
